/** Small UI helpers shared by every screen. Markup mirrors the approved prototype. */
import type { BadgeView } from "../shared/types";

export const $ = <T extends HTMLElement = HTMLElement>(s: string) => document.querySelector(s) as T | null;
export const $$ = <T extends HTMLElement = HTMLElement>(s: string) => [...document.querySelectorAll(s)] as T[];

export const esc = (s: unknown) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

export const I = {
  home: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z"/></svg>',
  cal: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>',
  user: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"/></svg>',
  admin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 6h16M4 12h16M4 18h10"/><circle cx="18" cy="18" r="2"/></svg>',
  checkin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="16" height="16" rx="3"/><path d="M8 12l3 3 5-6"/></svg>',
  file: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8z"/><path d="M14 3v5h5"/></svg>',
  plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>',
  x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
};
export const sized = (svg: string, n: number) => svg.replace("<svg", `<svg width="${n}" height="${n}"`);

/** The official MCDS monogram, unaltered (only scaled). */
export const MONO = (s = 34) =>
  `<img src="/icons/monogram-160.jpg" width="${s}" height="${s}" alt="MCDS" style="display:block;flex:none;box-shadow:0 0 0 1.5px rgba(255,255,255,.85)">`;

let toastTimer: number | undefined;
export function toast(m: string) {
  const t = $("#toast")!;
  t.textContent = m;
  t.classList.add("on");
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => t.classList.remove("on"), 2800);
}

export function openSheet(html: string, bare = true) {
  const s = $("#sheet")!;
  s.innerHTML = bare ? html : `<div class="grab"></div>${html}`;
  $("#scrim")!.classList.add("on");
  s.classList.add("on");
  s.scrollTop = 0;
}
export function closeSheet() {
  $("#scrim")!.classList.remove("on");
  $("#sheet")!.classList.remove("on");
}

/** Branded header for every create/edit box and pop-up. */
export function edHead(title: string, kick = "Ambassador Scheduling") {
  return `<div class="edhead"><div class="row" style="gap:12px;min-width:0">${MONO(40)}<div style="min-width:0"><div class="kick">${esc(kick)}</div><h2>${title}</h2></div></div><button class="edx" data-act="close-sheet" aria-label="Close">${sized(I.x, 16)}</button></div><div class="edstripe"></div>`;
}

const SILHOUETTE =
  '<svg viewBox="0 0 36 36"><circle cx="18" cy="14" r="6.5" fill="currentColor"/><path d="M6 33c1.5-7 6.5-10.5 12-10.5S28.5 26 30 33" fill="currentColor"/></svg>';

/** Round photo with a silhouette fallback when there's no photo or Gravatar. */
export function avatar(id: number, cls = "pav", bust = "") {
  return `<span class="${cls}" aria-hidden="true">${SILHOUETTE}<img src="/photos/${id}${bust}" alt="" loading="lazy" style="position:absolute;opacity:0;width:1px;height:1px" data-photo></span>`;
}
/** Header button to your Profile: your photo, or your initials when there's no photo. */
export function meAvatar(b: { id: number; first: string; last: string }, bust = "") {
  const ini = ((b.first[0] || "") + (b.last[0] || "")).toUpperCase() || "?";
  return `<a class="meav" href="#profile" aria-label="Your Profile" title="Your Profile"><span class="ini" aria-hidden="true">${esc(ini)}</span><img src="/photos/${b.id}${bust}" alt="" style="position:absolute;opacity:0;width:1px;height:1px" data-photo></a>`;
}
/** Shows photos once they load; leaves the silhouette when they don't. */
export function wirePhotos(root: ParentNode = document) {
  root.querySelectorAll<HTMLImageElement>("img[data-photo]").forEach((img) => {
    if (img.dataset.wired) return;
    img.dataset.wired = "1";
    const show = () => {
      // Hidden with opacity, not display:none, so lazy loading still fetches it.
      img.removeAttribute("style");
      img.style.display = "block";
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.objectFit = "cover";
      img.previousElementSibling?.remove();
    };
    if (img.complete && img.naturalWidth) show();
    else img.addEventListener("load", show, { once: true });
  });
}

export const fullName = (b: { first: string; last: string }) => [b.first, b.last].filter(Boolean).join(" ");

/** Who wears the PA of the Month pin right now (set from /api/me): user id -> award month (one entry per person). */
export const PINS: { map: Record<number, string> } = { map: {} };

/**
 * The PA of the Month pin: the real gold pin artwork. A light sweep crosses the gold (never the Spartan) when it
 * appears, and again on hover or tap. Hover text names the month.
 */
export function pinSvg(h: number, month: string) {
  const t = `Parent Ambassador of the Month: ${month}`;
  return `<span class="pin" title="${esc(t)}" tabindex="0" role="img" aria-label="${esc(t)}" style="width:${h}px;height:${h}px"><img src="/brand/pa-pin.png" alt="" width="${h}" height="${h}"><span class="sheen" aria-hidden="true"></span></span>`;
}
export const pinFor = (id: number | null | undefined, h: number) => (id && PINS.map[id] ? pinSvg(h, PINS.map[id]) : "");

/**
 * The name badge, styled after the physical MCDS badge: logo on top, centered name, role, and a blue line for
 * Admin and any titles. The PA of the Month pin sits in the top-right white space while it shows. No photo here:
 * photos appear next to names on events and lists, and are changed on the Profile page.
 */
export function badge(b: BadgeView, _bust = "", _editable = false) {
  const admin = / · Admin$/.test(b.label);
  const role = b.label.replace(/ · Admin$/, "");
  const extra = [...(admin ? ["Admin"] : []), ...b.titles].join(" · ");
  const pin = pinFor(b.id, 60);
  return `<div class="idbadge"><div class="btop"><span></span><img class="blogo" src="/brand/mcds-logo.png" alt="Miami Country Day School" width="97" height="82"><span class="bpin">${
    pin ? `${pin}<small>PA of the Month</small>` : ""
  }</span></div><div class="nm">${esc(fullName(b) || "New Ambassador")}</div><div class="rl">${esc(role)}</div>${extra ? `<div class="bt">${esc(extra)}</div>` : ""}</div>`;
}

export const fmtDateTime = (ms: number | null) =>
  ms
    ? new Date(ms).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    : "Never";
export const fmtDate = (ms: number) =>
  new Date(ms).toLocaleDateString("en-US", { timeZone: "America/New_York", month: "long", day: "numeric", year: "numeric" });
